/**
 * The buckets a location's pens come in, in display order. Every view that lists pens by
 * status iterates this, so a bucket is added here once rather than in each of them.
 *
 * `empty` only has members when the overview is read from the fish groups' point of view
 * (the `fish-group-tracking` flag): pens without an active fish group, which carry
 * no data.
 * Under that view `active` and `inactive` mean a fish group with and without a bioscope.
 */
export const OVERVIEW_CAGE_STATUSES = ['active', 'inactive', 'empty', 'historical'] as const;

export type OverviewCageStatus = (typeof OVERVIEW_CAGE_STATUSES)[number];

/** The field of an overview location that holds each bucket. */
export const OVERVIEW_CAGE_BUCKET_KEYS = {
    active: 'activeCages',
    inactive: 'inactiveCages',
    empty: 'emptyCages',
    historical: 'historicalCages',
} as const satisfies Record<OverviewCageStatus, string>;

type BucketKey = (typeof OVERVIEW_CAGE_BUCKET_KEYS)[OverviewCageStatus];

export type OverviewCageBuckets<C> = Record<BucketKey, C[]>;

/** Each bucket with its status, in display order. */
export function cagesByStatus<C>(
    buckets: OverviewCageBuckets<C>
): Array<{ status: OverviewCageStatus; cages: C[] }> {
    return OVERVIEW_CAGE_STATUSES.map((status) => ({
        status,
        cages: buckets[OVERVIEW_CAGE_BUCKET_KEYS[status]],
    }));
}

/** One value per bucket, keyed by status. */
export function mapCagesByStatus<C, R>(
    buckets: OverviewCageBuckets<C>,
    map: (cages: C[], status: OverviewCageStatus) => R
): Record<OverviewCageStatus, R> {
    return {
        active: map(buckets.activeCages, 'active'),
        inactive: map(buckets.inactiveCages, 'inactive'),
        empty: map(buckets.emptyCages, 'empty'),
        historical: map(buckets.historicalCages, 'historical'),
    };
}

/** Every pen of the buckets, in display order. */
export function allCagesOf<C>(buckets: OverviewCageBuckets<C>): C[] {
    return cagesByStatus(buckets).flatMap(({ cages }) => cages);
}
